import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
    IconGlobeAlt, IconActivity, IconTrendingUp,
    IconAlertTriangle, IconBarChart, IconZap, IconRefresh, IconArrowUp, IconDatabase
} from '../components/Icons';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
    AreaChart, Area
} from 'recharts';
import toast from 'react-hot-toast';
import { RealTimeBasket } from '../components/RealTimeBasket';
import WhatsAppSendButton from '../components/WhatsAppSendButton';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

import { API_BASE_URL } from '../../config';

const CACHE_KEY = 'aiq_macro_dashboard_data';
const CACHE_TIME_KEY = 'aiq_macro_dashboard_time';

// Load persisted data immediately (before first render)
const loadCachedMacro = () => {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        const ts  = localStorage.getItem(CACHE_TIME_KEY);
        if (raw) return { data: JSON.parse(raw), ts: ts ? new Date(parseInt(ts)) : null };
    } catch {}
    return null;
};

// ── Inflation Scout types ──────────────────────────────────────────────────────
interface FoodItem {
    price: number;
    period: string;
    yoy_change: number | null;
}
interface RegionCPI {
    cpi_index: number;
    period: string;
    yoy_change: number | null;
}
interface InflationData {
    bls_food_prices: Array<{ date: string; prices: Record<string, FoodItem>; basket_total: number }>;
    bls_regional_cpi: Array<{ date: string; regions: Record<string, RegionCPI> }>;
    trends: {
        bls_prices_mom?: { current: number; previous: number; change_pct: number; direction: string };
        google_shopping_wow?: { current: number; previous: number; change_pct: number; direction: string };
    };
    summary: { has_bls_data: boolean; has_google_data: boolean; has_commodity_data: boolean };
}

const FOOD_LABELS: Record<string, string> = {
    eggs: 'Eggs (doz)', milk: 'Milk (gal)', bread: 'Bread (lb)',
    ground_beef: 'Ground Beef (lb)', butter: 'Butter (lb)', bananas: 'Bananas (lb)',
};

// ── Inflation Scout Panel ──────────────────────────────────────────────────────
const InflationScoutPanel: React.FC<{ inflation: InflationData | null }> = ({ inflation }) => {
    const latestPrices = inflation?.bls_food_prices?.[0];
    const latestCPI = inflation?.bls_regional_cpi?.[0];
    const hasBLS = !!(latestPrices || latestCPI);
    const momTrend = inflation?.trends?.bls_prices_mom;

    const TrendIcon: React.FC<{ pct: number | null | undefined }> = ({ pct }) => {
        if (pct == null) return <Minus size={12} className="text-slate-400" />;
        if (pct > 0.5) return <TrendingUp size={12} className="text-red-500" />;
        if (pct < -0.5) return <TrendingDown size={12} className="text-emerald-500" />;
        return <Minus size={12} className="text-slate-400" />;
    };

    const yoyColor = (pct: number | null | undefined) => {
        if (pct == null) return 'text-slate-400';
        if (pct > 3) return 'text-red-600';
        if (pct > 1) return 'text-amber-600';
        if (pct < 0) return 'text-emerald-600';
        return 'text-slate-600';
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 shrink-0">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                        Inflation Scout
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100 uppercase tracking-wide">BLS Official</span>
                        {!hasBLS && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-100">Awaiting data</span>
                        )}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        {latestPrices
                            ? `National food prices · Updated ${latestPrices.date}`
                            : 'Run inflation_scout.py to populate'}
                    </p>
                </div>
                {momTrend && (
                    <div className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg ${momTrend.change_pct > 0 ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                        <TrendIcon pct={momTrend.change_pct} />
                        {momTrend.change_pct > 0 ? '+' : ''}{momTrend.change_pct.toFixed(1)}% MoM basket
                    </div>
                )}
            </div>

            {/* BLS Food Prices grid */}
            {latestPrices ? (
                <div className="grid grid-cols-3 gap-2 mb-4">
                    {Object.entries(latestPrices.prices).map(([key, item]) => (
                        <div key={key} className="bg-slate-50 border border-slate-100 rounded-lg p-2.5">
                            <div className="text-[10px] text-slate-500 font-medium truncate mb-1">{FOOD_LABELS[key] ?? key}</div>
                            <div className="text-sm font-bold text-slate-800 font-mono">${item.price.toFixed(2)}</div>
                            <div className={`flex items-center gap-0.5 text-[10px] font-bold mt-0.5 ${yoyColor(item.yoy_change)}`}>
                                <TrendIcon pct={item.yoy_change} />
                                {item.yoy_change != null
                                    ? `${item.yoy_change > 0 ? '+' : ''}${item.yoy_change.toFixed(1)}% YoY`
                                    : 'YoY N/A'}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-3 gap-2 mb-4">
                    {Object.entries(FOOD_LABELS).map(([key, label]) => (
                        <div key={key} className="bg-slate-50 border border-slate-100 border-dashed rounded-lg p-2.5 opacity-40">
                            <div className="text-[10px] text-slate-500 font-medium truncate mb-1">{label}</div>
                            <div className="text-sm font-bold text-slate-300 font-mono">—</div>
                            <div className="text-[10px] text-slate-300 mt-0.5">Pending</div>
                        </div>
                    ))}
                </div>
            )}

            {/* BLS Regional CPI strip */}
            {latestCPI && (
                <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Regional Food CPI — YoY Change</div>
                    <div className="grid grid-cols-4 gap-2">
                        {(['Northeast', 'Midwest', 'South', 'West'] as const).map(region => {
                            const r = latestCPI.regions[region];
                            return (
                                <div key={region} className="text-center p-2 bg-slate-50 border border-slate-100 rounded-lg">
                                    <div className="text-[10px] text-slate-500 font-medium mb-1">{region}</div>
                                    {r ? (
                                        <>
                                            <div className={`text-sm font-bold ${yoyColor(r.yoy_change)}`}>
                                                {r.yoy_change != null ? `${r.yoy_change > 0 ? '+' : ''}${r.yoy_change.toFixed(1)}%` : '—'}
                                            </div>
                                            <div className="text-[9px] text-slate-400 mt-0.5">idx {r.cpi_index.toFixed(1)}</div>
                                        </>
                                    ) : (
                                        <div className="text-sm font-bold text-slate-300">—</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const MacroView: React.FC = () => {
    const { currentUser } = useAuth();
    const cached = loadCachedMacro();
    const [data, setData] = useState<any>(cached?.data ?? null);
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(cached?.ts ?? null);
    const [inflation, setInflation] = useState<InflationData | null>(null);

    const fetchMacroData = async () => {
        if (!currentUser) return;
        setIsLoading(true);
        toast.loading("Syncing Macro Desk...", { id: 'macro-refresh', duration: 3000 });

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/macro/dashboard`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error("Failed to load macro data");
            const json = await res.json();
            const now = Date.now();
            setData(json);
            setLastUpdated(new Date(now));
            localStorage.setItem(CACHE_KEY, JSON.stringify(json));
            localStorage.setItem(CACHE_TIME_KEY, now.toString());
            toast.success("Macro Desk updated", { id: 'macro-refresh' });
        } catch (e) {
            console.error(e);
            toast.error("Could not refresh. Showing last saved data.", { id: 'macro-refresh' });
        } finally {
            setIsLoading(false);
        }
    };

    // Only auto-fetch on first ever load (no cache present)
    useEffect(() => {
        if (!data && currentUser) fetchMacroData();
    }, [currentUser]);

    // Fetch inflation data (public endpoint — no auth needed)
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/macro/inflation?days=90`);
                if (res.ok) setInflation(await res.json());
            } catch { /* silently ignore */ }
        })();
    }, []);

    // Helpers
    const getScoreColor = (score: number) => {
        if (score >= 80) return "text-emerald-400";
        if (score >= 60) return "text-emerald-500";
        if (score >= 40) return "text-amber-400";
        return "text-red-500";
    };

    const getScoreBg = (score: number) => {
        if (score >= 60) return "bg-emerald-500";
        if (score >= 40) return "bg-amber-500";
        return "bg-red-500";
    };

    const handleBackfill = async () => {
        if (!currentUser) return;
        const confirm = window.confirm("Run historical backfill? This computes 1 year of macro scores.");
        if (!confirm) return;
        
        toast.loading("Backfilling History...");
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/admin/backfill_macro`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                toast.success("Backfill Complete!");
                fetchMacroData(); // Refresh chart
            } else {
                toast.error("Backfill failed.");
            }
        } catch (e) {
            toast.error("Error connecting to server.");
        }
    };

    if (isLoading && !data) {
        return <div className="flex h-full items-center justify-center text-slate-400 animate-pulse">Loading Macro Desk...</div>;
    }

    if (!data) return (
        <div className="flex h-full items-center justify-center flex-col gap-4 text-slate-400">
            <p>No macro data loaded yet.</p>
            <button onClick={fetchMacroData} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700">
                Load Macro Desk
            </button>
        </div>
    );

    return (
        <div className="flex h-full bg-slate-50 overflow-hidden font-sans">
            
            {/* LEFT: SUMMARY PANEL */}
            <div className="w-96 bg-white border-r border-slate-200 flex flex-col p-6 overflow-y-auto">
                
                {/* HEADER WITH REFRESH BUTTON */}
                <div className="mb-6 shrink-0 flex justify-between items-start">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                            <IconGlobeAlt className="text-blue-600" /> Macro Desk
                        </h2>
                        <div className="flex items-center gap-2">
                            <p className="text-xs text-slate-500">Global Regime & Risk Analytics</p>
                            {lastUpdated && (
                                <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                    Updated: {lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            onClick={fetchMacroData}
                            disabled={isLoading}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors ${
                                isLoading
                                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                    : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                            title="Refresh data from server"
                        >
                            <IconRefresh size={13} className={isLoading ? 'animate-spin' : ''} />
                            Refresh
                        </button>
                        {data && (
                            <WhatsAppSendButton
                                label="Macro Report"
                                content={`AiQ Macro Score: ${data.regime.score}/100\nRegime: ${data.regime.label}\n\n${data.regime.explanation || ''}`}
                            />
                        )}
                    </div>
                </div>

                {/* MACRO SCORE CARD */}
                <div className="bg-slate-900 rounded-xl p-6 shadow-xl mb-6 relative border border-slate-700 shrink-0">
                    <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                        <IconActivity size={80} color="white" />
                    </div>
                    
                    <div className="relative z-10 flex flex-col gap-4">
                        <div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">AiQ Macro Score</div>
                            <div className="flex items-baseline gap-2">
                                <span className={`text-5xl font-bold ${getScoreColor(data.regime.score)}`}>
                                    {data.regime.score}
                                </span>
                                <span className="text-sm font-medium text-slate-400">/ 100</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <div className={`px-2 py-1 rounded text-[10px] font-bold border uppercase tracking-wide ${data.regime.score >= 60 ? 'bg-emerald-900/50 border-emerald-700 text-emerald-400' : 'bg-amber-900/50 border-amber-700 text-amber-400'}`}>
                                {data.regime.label}
                            </div>
                            <div className="h-1.5 flex-1 bg-slate-800 rounded-full overflow-hidden border border-slate-700">
                                <div 
                                    className={`h-full ${getScoreBg(data.regime.score)} transition-all duration-1000`} 
                                    style={{ width: `${data.regime.score}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* EXPLANATION */}
                        <div className="pt-4 border-t border-slate-700/50">
                            <p className="text-[10px] font-bold text-blue-400 uppercase mb-1">AiQ Analysis</p>
                            <p className="text-sm leading-relaxed text-white font-medium">
                                {data.regime.explanation || "Analysis data unavailable."}
                            </p>
                        </div>
                    </div>
                </div>

                {/* HISTORY CHART */}
                <div className="mb-6 shrink-0">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1">
                        <IconTrendingUp size={12}/> Regime Trend (90d)
                    </h3>
                    <div className="w-full h-24 bg-white rounded border border-slate-100 p-1">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data.history}>
                                <defs>
                                    <linearGradient id="scoreColor" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                {/* XAxis needs dataKey="date" for the tooltip to read it, even if hidden */}
                                <XAxis dataKey="date" hide />
                                
                                <Tooltip 
                                    contentStyle={{
                                        backgroundColor: '#1e293b', 
                                        border: 'none', 
                                        borderRadius: '6px', 
                                        fontSize: '11px', 
                                        color: '#fff',
                                        padding: '8px'
                                    }}
                                    itemStyle={{color: '#60a5fa', fontWeight: 'bold'}}
                                    labelStyle={{color: '#94a3b8', marginBottom: '4px', fontWeight: 'normal'}}
                                    // FORMAT THE DATE HERE
                                    labelFormatter={(label) => {
                                        // Parse YYYY-MM-DD and format to "Nov 30, 2025"
                                        return new Date(label).toLocaleDateString('en-US', { 
                                            year: 'numeric', 
                                            month: 'short', 
                                            day: 'numeric' 
                                        });
                                    }}
                                    formatter={(value) => [value, "Macro Score"]}
                                />
                                
                                <Area 
                                    type="monotone" 
                                    dataKey="score" 
                                    stroke="#3b82f6" 
                                    strokeWidth={2} 
                                    fill="url(#scoreColor)" 
                                    isAnimationActive={false} 
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* CROSS ASSET SNAPSHOT */}
                <div className="shrink-0">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Key Indicators</h3>
                    <div className="space-y-2">
                        {data.cross_assets.map((asset: any) => (
                            <div key={asset.ticker} className="flex justify-between items-center p-2.5 bg-slate-50 border border-slate-100 rounded hover:bg-slate-100 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="text-slate-400 min-w-[20px] text-center">
                                        {asset.ticker === 'TLT' && <IconBarChart size={14} />}
                                        {asset.ticker === 'GLD' && <IconZap size={14} />}
                                        {asset.ticker === 'BTC-USD' && <IconTrendingUp size={14} />}
                                        {asset.ticker === '^VIX' && <IconAlertTriangle size={14} />}
                                        {asset.ticker === 'SPY' && <IconActivity size={14} />}
                                        {asset.ticker === 'QQQ' && <IconArrowUp size={14} />}
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold text-slate-700">{asset.ticker}</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs font-mono font-medium text-slate-600">${asset.price.toFixed(2)}</div>
                                    <div className={`text-[10px] font-bold ${asset.change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {asset.change > 0 ? '+' : ''}{asset.change}%
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* RIGHT: SECTOR ROTATION & REAL-TIME BASKET */}
            <div className="flex-1 p-6 flex flex-col gap-6 overflow-y-auto min-w-0">

                {/* 1. SECTOR ROTATION */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col shrink-0 p-6" style={{minHeight: '380px'}}>
                    <div className="flex justify-between items-center mb-6 shrink-0">
                        <div>
                            <h3 className="text-lg font-bold text-slate-800">Sector Rotation</h3>
                            <p className="text-xs text-slate-500">1-Month Relative Performance</p>
                        </div>
                        <div className="flex gap-2">
                            {data.sectors.length > 0 && (
                                <>
                                    <div className="text-xs font-mono text-slate-500 bg-emerald-50 px-2 py-1 rounded border border-emerald-100">
                                        Leader: <span className="text-emerald-700 font-bold">{data.sectors[0]?.name}</span>
                                    </div>
                                    <div className="text-xs font-mono text-slate-500 bg-red-50 px-2 py-1 rounded border border-red-100">
                                        Laggard: <span className="text-red-700 font-bold">{data.sectors[data.sectors.length - 1]?.name}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 min-h-0" style={{height: '300px'}}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data.sectors}
                                layout="vertical"
                                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                <XAxis type="number" hide />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    tick={{fontSize: 11, fontWeight: 'bold', fill: '#475569'}}
                                    width={100}
                                    interval={0}
                                />
                                <Tooltip
                                    cursor={{fill: '#f1f5f9'}}
                                    contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'}}
                                    labelStyle={{color: '#64748b', marginBottom: '5px', fontWeight: 'bold'}}
                                    labelFormatter={(value) => {
                                        const item = data.sectors.find((s: any) => s.name === value);
                                        return item ? `${value} (${item.ticker})` : value;
                                    }}
                                    formatter={(value: number) => [`${value}%`, 'Return']}
                                />
                                <Bar dataKey="performance" radius={[0, 4, 4, 0]} barSize={24} isAnimationActive={false}>
                                    {data.sectors.map((entry: any, index: number) => (
                                        <Cell key={`cell-${index}`} fill={entry.performance >= 0 ? '#10b981' : '#ef4444'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* 2. INFLATION SCOUT */}
                <InflationScoutPanel inflation={inflation} />

                {/* 3. REAL-TIME BASKET */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex-1 min-h-0 overflow-hidden">
                    <RealTimeBasket />
                </div>
            </div>
        </div>
    );
};

export default MacroView;